import { FormEvent, useCallback, useEffect, useState } from "react";
import { LuAlertCircle, LuInfo, LuPencil, LuPlus } from "react-icons/lu";
import apiClient from "../../api-services/ApiClient";
import AdminLayout from "../../app-components/AdminLayout";
import ConfirmDialog from "../../app-components/ui/ConfirmDialog";
import PageHeader from "../../app-components/ui/PageHeader";
import StatusPill from "../../app-components/ui/StatusPill";
import { btnPrimary, btnSecondary, card, inputBase } from "../../app-components/ui/styles";
import { getErrorMessage } from "../../utils";
import notify from "../../utils/notify";
import { fmtMoney, Money } from "../dashboard/components/format";

type Price = {
  id: number;
  plan: string | null;
  duration_months: number;
  currency: Money;
  amount: string;
  is_active: boolean;
};

type Change = {
  id: number;
  plan_price_id: number;
  action: "created" | "updated";
  old_amount: string | null;
  new_amount: string | null;
  old_active: boolean | null;
  new_active: boolean | null;
  created_at: string;
  admin?: { name: string; email: string } | null;
};

const STANDARD_DURATIONS = [1, 3, 6, 12];
const PLANS = ["STANDARD", "BRONZE", "SILVER", "GOLD", "ELITEGOLD"];
const REGIONS: { currency: Money; title: string; subtitle: string }[] = [
  { currency: "NGN", title: "Nigeria", subtitle: "Paid in naira (₦)" },
  { currency: "USD", title: "Outside Nigeria", subtitle: "Paid in US dollars ($)" },
];

const durationLabel = (m: number) =>
  ({ 1: "Monthly", 3: "Quarterly", 6: "6 months", 12: "Annually" } as Record<number, string>)[m] ??
  `${m} month${m === 1 ? "" : "s"}`;

const planLabel = (p: string | null) =>
  p ? (p === "ELITEGOLD" ? "Elite Gold" : p.charAt(0) + p.slice(1).toLowerCase()) : "All plans";

const timeAgo = (iso: string) => {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
};

/* ------------------------------------------------------------------ */
/* One price row: view, edit in place, switch on/off                   */
/* ------------------------------------------------------------------ */

const PriceRow = ({
  label,
  sublabel,
  currency,
  price,
  onSave,
  onToggle,
}: {
  label: string;
  sublabel?: string;
  currency: Money;
  price?: Price;
  onSave: (amount: number, price?: Price) => Promise<boolean>;
  onToggle: (price: Price) => void;
}) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const start = () => {
    setValue(price ? String(Number(price.amount)) : "");
    setError("");
    setEditing(true);
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    const amount = Number(value.replace(/,/g, ""));
    if (!Number.isFinite(amount) || amount <= 0) {
      setError("Enter an amount above zero.");
      return;
    }
    setSaving(true);
    const ok = await onSave(amount, price);
    setSaving(false);
    if (ok) setEditing(false);
  };

  const symbol = currency === "USD" ? "$" : "₦";

  return (
    <li className="flex flex-wrap items-center gap-3 py-3.5">
      <div className="min-w-[120px] flex-1">
        <p className="text-sm font-medium text-honeConnectBlack">{label}</p>
        {sublabel && <p className="text-xs text-hcMuted">{sublabel}</p>}
      </div>

      {editing ? (
        <form onSubmit={save} className="flex flex-wrap items-start gap-2">
          <div>
            <div className="relative">
              <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-hcMuted">
                {symbol}
              </span>
              <input
                autoFocus
                inputMode="decimal"
                aria-label={`${label} price in ${currency}`}
                value={value}
                onChange={(e) => {
                  setValue(e.target.value);
                  setError("");
                }}
                className={`${inputBase} w-36 py-2 pl-7`}
              />
            </div>
            {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
          </div>
          <button type="submit" disabled={saving} className={`${btnPrimary} py-2`}>
            {saving ? "Saving" : "Save"}
          </button>
          <button type="button" onClick={() => setEditing(false)} disabled={saving} className={`${btnSecondary} py-2`}>
            Cancel
          </button>
        </form>
      ) : price ? (
        <div className="flex items-center gap-3">
          <span className={`text-base font-semibold ${price.is_active ? "text-honeConnectBlack" : "text-gray-400 line-through"}`}>
            {fmtMoney(currency, Number(price.amount))}
          </span>
          <StatusPill label={price.is_active ? "On" : "Off"} tone={price.is_active ? "success" : "neutral"} />
          <button
            type="button"
            onClick={start}
            aria-label={`Edit ${label} price`}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-honeConnectGray hover:bg-hcBg hover:text-honeConnectBlack"
          >
            <LuPencil className="h-4 w-4" aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => onToggle(price)}
            className="text-xs font-medium text-honeConnectPurple hover:underline"
          >
            {price.is_active ? "Switch off" : "Switch on"}
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-400">Not set</span>
          <button type="button" onClick={start} className={`${btnSecondary} py-1.5 text-xs`}>
            Set price
          </button>
        </div>
      )}
    </li>
  );
};

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

const PlanPricing = () => {
  const [prices, setPrices] = useState<Price[]>([]);
  const [changes, setChanges] = useState<Change[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [toggleTarget, setToggleTarget] = useState<Price | null>(null);
  const [toggling, setToggling] = useState(false);

  // custom price form
  const [custom, setCustom] = useState({ duration: "2", currency: "NGN", plan: "", amount: "" });
  const [customError, setCustomError] = useState("");
  const [addingCustom, setAddingCustom] = useState(false);

  const load = useCallback(async () => {
    setLoadError("");
    try {
      const res: any = await apiClient.get("plan-prices");
      setPrices(res?.data?.prices ?? []);
      setChanges(res?.data?.changes ?? []);
    } catch (err) {
      setLoadError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const findPrice = (currency: Money, months: number, plan: string | null = null) =>
    prices.find((p) => p.currency === currency && p.duration_months === months && (p.plan ?? null) === plan);

  const describe = (p?: Pick<Price, "duration_months" | "currency" | "plan">) =>
    p ? `${durationLabel(p.duration_months)} (${p.currency}${p.plan ? `, ${planLabel(p.plan)}` : ""})` : "price";

  const savePrice = async (
    amount: number,
    existing: Price | undefined,
    create: { duration_months: number; currency: Money; plan: string | null }
  ) => {
    try {
      if (existing) {
        await apiClient.patch(`plan-prices/${existing.id}`, { amount });
        notify.success(`${describe(existing)} price updated to ${fmtMoney(existing.currency, amount)}.`);
      } else {
        await apiClient.post("plan-prices", { ...create, amount });
        notify.success(`${describe(create)} price set to ${fmtMoney(create.currency, amount)}.`);
      }
      await load();
      return true;
    } catch (err) {
      notify.error(`Couldn't save the price: ${getErrorMessage(err)}`);
      return false;
    }
  };

  const confirmToggle = async () => {
    if (!toggleTarget) return;
    setToggling(true);
    try {
      await apiClient.patch(`plan-prices/${toggleTarget.id}`, { is_active: !toggleTarget.is_active });
      notify.success(`${describe(toggleTarget)} switched ${toggleTarget.is_active ? "off" : "on"}.`);
      setToggleTarget(null);
      await load();
    } catch (err) {
      notify.error(`Couldn't change it: ${getErrorMessage(err)}`);
    } finally {
      setToggling(false);
    }
  };

  const addCustom = async (e: FormEvent) => {
    e.preventDefault();
    const months = Number(custom.duration);
    const amount = Number(custom.amount.replace(/,/g, ""));
    if (!Number.isInteger(months) || months < 1 || months > 36) {
      setCustomError("Length must be 1 to 36 months.");
      return;
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      setCustomError("Enter an amount above zero.");
      return;
    }
    setCustomError("");
    setAddingCustom(true);
    const ok = await savePrice(amount, undefined, {
      duration_months: months,
      currency: custom.currency as Money,
      plan: custom.plan || null,
    });
    setAddingCustom(false);
    if (ok) setCustom((c) => ({ ...c, amount: "" }));
  };

  // prices outside the four standard all-plans rows, shown under their region
  const extras = (currency: Money) =>
    prices
      .filter((p) => p.currency === currency && (p.plan !== null || !STANDARD_DURATIONS.includes(p.duration_months)))
      .sort((a, b) => a.duration_months - b.duration_months || (a.plan ?? "").localeCompare(b.plan ?? ""));

  const priceById = (id: number) => prices.find((p) => p.id === id);

  const changeText = (c: Change) => {
    const p = priceById(c.plan_price_id);
    const what = describe(p);
    const cur = (p?.currency ?? "NGN") as Money;
    if (c.action === "created") return `set ${what} to ${fmtMoney(cur, Number(c.new_amount))}`;
    const parts: string[] = [];
    if (c.old_amount !== c.new_amount && c.new_amount !== null)
      parts.push(`changed ${what} from ${fmtMoney(cur, Number(c.old_amount))} to ${fmtMoney(cur, Number(c.new_amount))}`);
    if (c.old_active !== c.new_active && c.new_active !== null)
      parts.push(`switched ${what} ${c.new_active ? "on" : "off"}`);
    return parts.join(" and ") || `updated ${what}`;
  };

  return (
    <AdminLayout title="Admin | Plans & pricing">
      <PageHeader
        title="Plans & pricing"
        subtitle="What members pay, by subscription length and currency. Every payment is checked against these prices."
      />

      <div className="mb-5 flex items-start gap-3 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900">
        <LuInfo className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <p>
          A payment must match the price for its length and currency, or it's refused as "Amount mismatch".
          Where no price is set (or it's switched off), the payment is checked against the amount the app sends,
          as before.
        </p>
      </div>

      {loadError && !loading ? (
        <div className={`${card} flex flex-col items-center px-6 py-14 text-center`}>
          <LuAlertCircle className="mb-3 h-8 w-8 text-red-500" aria-hidden />
          <p className="mb-1 font-semibold text-honeConnectBlack">Couldn't load prices</p>
          <p className="mb-4 max-w-md text-sm text-hcMuted">{loadError}</p>
          <button onClick={() => { setLoading(true); load(); }} className={btnPrimary}>
            Try again
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            {REGIONS.map((r) => (
              <section key={r.currency} className={`${card} px-5 pb-2 pt-5`}>
                <header className="mb-1">
                  <h2 className="text-[15px] font-semibold text-honeConnectBlack">{r.title}</h2>
                  <p className="text-xs text-hcMuted">{r.subtitle}</p>
                </header>
                {loading ? (
                  <ul className="space-y-4 py-4">
                    {STANDARD_DURATIONS.map((m) => (
                      <li key={m} className="h-6 animate-pulse rounded bg-hcBg" />
                    ))}
                  </ul>
                ) : (
                  <ul className="divide-y divide-hcBorder">
                    {STANDARD_DURATIONS.map((m) => (
                      <PriceRow
                        key={m}
                        label={durationLabel(m)}
                        sublabel={m === 1 ? "1 month" : `${m} months`}
                        currency={r.currency}
                        price={findPrice(r.currency, m)}
                        onSave={(amount, existing) =>
                          savePrice(amount, existing, { duration_months: m, currency: r.currency, plan: null })
                        }
                        onToggle={setToggleTarget}
                      />
                    ))}
                    {extras(r.currency).map((p) => (
                      <PriceRow
                        key={p.id}
                        label={durationLabel(p.duration_months)}
                        sublabel={p.plan ? `${planLabel(p.plan)} only` : "All plans"}
                        currency={r.currency}
                        price={p}
                        onSave={(amount, existing) =>
                          savePrice(amount, existing, { duration_months: p.duration_months, currency: r.currency, plan: p.plan })
                        }
                        onToggle={setToggleTarget}
                      />
                    ))}
                  </ul>
                )}
              </section>
            ))}
          </div>

          <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-2">
            {/* custom price */}
            <section className={`${card} p-5`}>
              <h2 className="text-[15px] font-semibold text-honeConnectBlack">Add a custom price</h2>
              <p className="mb-4 text-xs text-hcMuted">
                For another length (e.g. 2 months) or a price for one plan only.
              </p>
              <form onSubmit={addCustom} className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <label className="text-xs font-medium text-hcMuted">
                  Length (months)
                  <input
                    type="number"
                    min={1}
                    max={36}
                    value={custom.duration}
                    onChange={(e) => setCustom({ ...custom, duration: e.target.value })}
                    className={`${inputBase} mt-1 w-full py-2`}
                  />
                </label>
                <label className="text-xs font-medium text-hcMuted">
                  Currency
                  <select
                    value={custom.currency}
                    onChange={(e) => setCustom({ ...custom, currency: e.target.value })}
                    className={`${inputBase} mt-1 w-full py-2`}
                  >
                    <option value="NGN">NGN (₦)</option>
                    <option value="USD">USD ($)</option>
                  </select>
                </label>
                <label className="text-xs font-medium text-hcMuted">
                  Plan
                  <select
                    value={custom.plan}
                    onChange={(e) => setCustom({ ...custom, plan: e.target.value })}
                    className={`${inputBase} mt-1 w-full py-2`}
                  >
                    <option value="">All plans</option>
                    {PLANS.map((p) => (
                      <option key={p} value={p}>
                        {planLabel(p)}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-xs font-medium text-hcMuted">
                  Price
                  <input
                    inputMode="decimal"
                    value={custom.amount}
                    onChange={(e) => setCustom({ ...custom, amount: e.target.value })}
                    placeholder="0"
                    className={`${inputBase} mt-1 w-full py-2`}
                  />
                </label>
                <div className="col-span-2 flex items-center gap-3 sm:col-span-4">
                  <button type="submit" disabled={addingCustom} className={`${btnPrimary} inline-flex items-center gap-2`}>
                    <LuPlus className="h-4 w-4" aria-hidden />
                    {addingCustom ? "Adding" : "Add price"}
                  </button>
                  {customError && <p className="text-sm text-red-600">{customError}</p>}
                </div>
              </form>
            </section>

            {/* history */}
            <section className={`${card} p-5`}>
              <h2 className="mb-3 text-[15px] font-semibold text-honeConnectBlack">Recent changes</h2>
              {changes.length === 0 ? (
                <p className="text-sm text-hcMuted">No changes yet. Edits made here will be listed with who made them.</p>
              ) : (
                <ul className="space-y-3">
                  {changes.slice(0, 12).map((c) => (
                    <li key={c.id} className="text-sm leading-relaxed">
                      <span className="font-medium text-honeConnectBlack">{c.admin?.name ?? "An admin"}</span>{" "}
                      <span className="text-honeConnectGray">{changeText(c)}</span>
                      <span className="block text-xs text-hcMuted">{timeAgo(c.created_at)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        </>
      )}

      <ConfirmDialog
        open={!!toggleTarget}
        tone={toggleTarget?.is_active ? "danger" : "primary"}
        title={toggleTarget?.is_active ? `Switch off the ${describe(toggleTarget ?? undefined)} price?` : `Switch on the ${describe(toggleTarget ?? undefined)} price?`}
        message={
          toggleTarget?.is_active
            ? "Payments for this length and currency will then be checked against the amount the app sends, as before. You can switch it back on any time."
            : "Payments for this length and currency will be checked against this price again."
        }
        confirmLabel={toggleTarget?.is_active ? "Switch off" : "Switch on"}
        loading={toggling}
        onConfirm={confirmToggle}
        onCancel={() => setToggleTarget(null)}
      />
    </AdminLayout>
  );
};

export default PlanPricing;
